/**
 * Auth module — Google OAuth via auth-api.arkturian.com
 *
 * Flow:
 *   1. User clicks "Sign in with Google"
 *   2. Redirect to auth-api → Google → auth-api → callback URL
 *   3. Callback page extracts code, sends to auth-api → gets JWT
 *   4. JWT stored in localStorage, used for all guide-api calls
 */

const AUTH_API = 'https://auth-api.arkturian.com/api/v1';
const TOKEN_KEY = 'wanderlaut-access-token';
const REFRESH_KEY = 'wanderlaut-refresh-token';
const USER_KEY = 'wanderlaut-user';

export interface WanderlautUser {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
}

/** Check if user has logged in at least once (has stored user info).
 * Note: this returns true even if the access/refresh tokens have expired.
 * guide-api logging uses service-to-service auth + stored user_id, not JWT,
 * so expired tokens don't block history saving. */
export function isLoggedIn(): boolean {
  return !!localStorage.getItem(USER_KEY);
}

/** Detect in-app browsers (Messenger, Instagram, Facebook, TikTok, etc.).
 * Google blocks OAuth from these with `disallowed_useragent`, so we must
 * tell the user to open the link in a real browser first. */
export function isInAppBrowser(): { inApp: boolean; appName: string } {
  const ua = navigator.userAgent || '';
  const checks: Array<[RegExp, string]> = [
    [/FBAN|FBAV|FB_IAB|FB4A/i, 'Facebook'],
    [/Messenger/i, 'Messenger'],
    [/Instagram/i, 'Instagram'],
    [/MicroMessenger/i, 'WeChat'],
    [/Line\//i, 'LINE'],
    [/TikTok|musical_ly/i, 'TikTok'],
    [/Twitter/i, 'X (Twitter)'],
    [/Snapchat/i, 'Snapchat'],
    [/LinkedInApp/i, 'LinkedIn'],
  ];
  for (const [re, name] of checks) {
    if (re.test(ua)) return { inApp: true, appName: name };
  }
  return { inApp: false, appName: '' };
}

/** Get stored access token */
export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

/** Get stored user info */
export function getUser(): WanderlautUser | null {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/** Start Google OAuth flow — fetches Google URL from auth-api, then redirects */
export async function startGoogleLogin(): Promise<void> {
  const redirectUri = `${window.location.origin}/auth/callback`;
  const state = crypto.randomUUID();
  localStorage.setItem('oauth_state', state);

  try {
    const res = await fetch(
      `${AUTH_API}/auth/google/authorize?redirect_uri=${encodeURIComponent(redirectUri)}&state=${state}`
    );
    const data = await res.json();
    if (data.url) {
      window.location.href = data.url;
    } else {
      console.error('[auth] No URL in response:', data);
    }
  } catch (err) {
    console.error('[auth] Google login failed:', err);
  }
}

/** Handle OAuth callback — exchange code for JWT */
export async function handleCallback(): Promise<boolean> {
  const params = new URLSearchParams(window.location.search);
  const code = params.get('code');
  const state = params.get('state');

  if (!code) return false;

  // Verify state
  const savedState = localStorage.getItem('oauth_state');
  if (state && savedState && state !== savedState) {
    console.error('[auth] State mismatch');
    return false;
  }
  localStorage.removeItem('oauth_state');

  // Exchange code for tokens
  const redirectUri = `${window.location.origin}/auth/callback`;
  try {
    const res = await fetch(`${AUTH_API}/auth/google/callback`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code, redirect_uri: redirectUri }),
    });
    if (!res.ok) {
      console.error('[auth] Token exchange failed:', res.status);
      return false;
    }
    const data = await res.json();
    if (data.access_token) {
      localStorage.setItem(TOKEN_KEY, data.access_token);
      if (data.refresh_token) localStorage.setItem(REFRESH_KEY, data.refresh_token);

      // Fetch user info
      await fetchAndStoreUser(data.access_token);
      return true;
    }
  } catch (err) {
    console.error('[auth] Callback error:', err);
  }
  return false;
}

/** Fetch user profile and store */
async function fetchAndStoreUser(token: string): Promise<void> {
  try {
    const res = await fetch(`${AUTH_API}/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.ok) {
      const data = await res.json();
      localStorage.setItem(USER_KEY, JSON.stringify({
        id: data.id,
        email: data.email,
        first_name: data.first_name,
        last_name: data.last_name,
      }));
    }
  } catch {}
}

/** Refresh access token */
export async function refreshToken(): Promise<boolean> {
  const refresh = localStorage.getItem(REFRESH_KEY);
  if (!refresh) return false;
  try {
    const res = await fetch(`${AUTH_API}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refresh }),
    });
    // 401 = der Refresh-Token ist serverseitig endgültig entwertet (rotiert /
    // abgelaufen). Ihn zu behalten heißt nur: derselbe 401 bei JEDEM App-Start.
    // Weg damit — der nächste Boot versucht gar nicht erst zu refreshen.
    if (res.status === 401) localStorage.removeItem(REFRESH_KEY);
    if (res.ok) {
      const data = await res.json();
      if (data.access_token) {
        localStorage.setItem(TOKEN_KEY, data.access_token);
        // Persist a ROTATED refresh-token. auth-api rotates the refresh-token on
        // refresh (security best-practice); discarding the new one means the next
        // refresh re-uses the now-invalidated old token → 401 → logout after ~2
        // reloads. This was the real "constant re-login" bug (Alex 2026-06-27).
        if (data.refresh_token) localStorage.setItem(REFRESH_KEY, data.refresh_token);
        return true;
      }
    }
  } catch {}
  // Refresh failed. Prod: clear everything (force re-login). Dev: KEEP the session
  // so a reload doesn't kick the tester back to the Google sign-in screen — one
  // login then holds across reloads, realtime works in the 15-min token window
  // (Alex 2026-06-27, until the permanent dev-token from Auth lands).
  const p = new URLSearchParams(location.search);
  const isDev =
    location.hostname.includes('-dev') ||
    location.hostname === 'localhost' ||
    location.hostname === '127.0.0.1' ||
    p.has('dev') ||
    p.get('realtime') === '1';
  if (!isDev) logout();
  return false;
}

/** Logout — clear all auth state */
export function logout(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem(USER_KEY);
}

/**
 * Dev-only auto-login (Alex 2026-06-27, #1233). Mints an Apopovic token via the
 * Vite `/__dev-mint` proxy (which injects the server-side DEV_MINT_SERVICE_KEY →
 * auth-api dev-mint endpoint), so dev/E2E skips the Google OAuth dance entirely.
 * No-op in prod, when already logged in, with `?nodevlogin=1`, or when the proxy
 * isn't configured (503 → normal Google sign-in stays). The minted JWT is identical
 * to a real login token and goes 1:1 through AiApi voice-mint + realtime-grant.
 */
export async function devAutoLogin(force = false): Promise<boolean> {
  const p = new URLSearchParams(location.search);
  if (p.get('nodevlogin') === '1') return false;
  const isDev =
    location.hostname.includes('-dev') ||
    location.hostname === 'localhost' ||
    location.hostname === '127.0.0.1' ||
    p.has('dev') ||
    p.get('realtime') === '1';
  // force=true re-mints even when already "logged in" — needed before the realtime
  // start because the dev-mint token has a 15-min TTL and NO refresh-token.
  if (!isDev || (!force && isLoggedIn())) return false;
  try {
    const res = await fetch('/__dev-mint', { method: 'POST' });
    if (!res.ok) return false; // 503 = proxy not configured → fall back to Google login
    const data = await res.json();
    if (data.access_token) {
      localStorage.setItem(TOKEN_KEY, data.access_token);
      // Store the user from the SAME-ORIGIN mint response (the proxy forwards
      // auth-api's payload) — avoids a cross-origin /auth/me call (CORS-blocked from
      // localhost in CI/E2E). The dev-mint user is {id, email, name}; split the name
      // into first/last for WanderlautUser.
      const u = (data.user || {}) as { id?: string; sub?: string; email?: string; name?: string };
      const name = String(u.name || '');
      localStorage.setItem(
        USER_KEY,
        JSON.stringify({
          id: u.id || u.sub || '',
          email: u.email || '',
          first_name: name.split(' ')[0] || u.email || 'Dev',
          last_name: name.split(' ').slice(1).join(' '),
        }),
      );
      return true;
    }
  } catch {
    /* network/proxy error → fall back to Google login */
  }
  return false;
}

/**
 * Ensure a usable, FRESH access token right before an outbound auth'd call (the
 * realtime mint). Real logins rotate via the refresh-token; dev-mint tokens have
 * NO refresh-token + a 15-min TTL, so they must be RE-MINTED (not refreshed) — a
 * token minted at app-load otherwise expires before the realtime actually starts
 * (→ auth-api `invalid_token`). Returns true if a token is available afterwards.
 */
export async function ensureFreshToken(): Promise<boolean> {
  if (await refreshToken()) return true; // real login → rotate via refresh-token
  return devAutoLogin(true); // dev-mint → re-mint a fresh 15-min token
}
